// src/components/AgentsTab.jsx
import { useState, useMemo, useRef, useEffect } from 'react'
import { DAYS, agentDaySummary, daySlots } from '../lib/schedule.js'
import { fmtDuration } from '../lib/attendance.js'
import { AgentModal } from './AgentModal.jsx'
import { t, getLanguage } from '../lib/i18n.js'
import { logEvent, getCurrentAdmin } from '../lib/agentLog.js'
import { useViewPreference } from '../hooks/useViewPreference.js'
import { fmtPhone } from '../lib/phone.js'
import { releaseAgentDrafts } from '../db/index.js'
import { lockLoginReported, reportAgentSyncFailure, AGENT_SYNC_FAILED_EVENT } from '../hooks/useSchedule.js'
import { MarkLeftDialog } from './MarkLeftDialog.jsx'
import { Icon } from './Icon.jsx'
import { COUNTER, pill } from './desk/primitives.jsx'
// ⚠️ Whether an agent has resigned YET is decided by the office clock, not the viewer's PC
// (owner 2026-09-02). A manager abroad, or on a machine whose clock is a day out, must not see
// a different roster from the one the office sees.
import { todayStr } from '../lib/clock.js'
import { keepInView } from '../lib/keepInView.js'

export { fmtPhone }

const dateLocale = () => getLanguage() === 'ka' ? 'ka-GE' : 'en-GB'

// Compare two phone numbers by their last 9 digits (ignores +995 / spacing).
const normNum = s => (s || '').replace(/\D/g, '').slice(-9)

// Inventory status of an agent's work number (null when they never had one).
// The number falls back to history so a resigned agent whose number was removed
// (phone_work cleared) still shows it — consistently with agents who kept theirs.
//   number       – the number to display
//   state        – 'held' (still theirs) | 'pool' (free) | 'reassigned'
//   ownerName    – current owner when reassigned
function phoneState(agent, phones = [], agents = [], historyByAgent = {}) {
  const number = agent.phone_work || historyByAgent[agent.id] || ''
  if (!number) return null
  const inv = phones.find(p => normNum(p.number) === normNum(number))
  if (inv && inv.agent_id === agent.id) return { number, state: 'held' }
  if (inv && inv.agent_id) return { number, state: 'reassigned', ownerName: agents.find(a => a.id === inv.agent_id)?.name || '?' }
  return { number, state: 'pool' }
}

// Colour + hover tooltip for a resigned agent's number, so the status reads at a
// glance (amber = still theirs, green = free, faded = gone to someone else)
// without cluttering the card with text badges.
function phoneMeta(ps) {
  if (!ps) return null
  if (ps.state === 'held')       return { cls: 'text-amber-600 dark:text-amber-400',   title: t('phone_held_badge') }
  if (ps.state === 'reassigned') return { cls: 'text-muted-foreground/40 line-through', title: t('phone_now_label').replace('{name}', ps.ownerName) }
  return { cls: 'text-emerald-600 dark:text-emerald-400', title: t('phone_pool_badge') }
}

// ─── Shared pill class for all filter dropdowns ───────────────────────────────
// ⚠️ NOT a pill in the design/PILL_SYSTEM_SPEC sense — this is the toolbar's full-width
// h-9 filter SELECT control, and it was called PILL until 2026-07-30. primitives.jsx now
// exports PILL/PILL_* for the actual pill contract, and CandidatesTab imports these three,
// so the old names were one import away from a duplicate binding. Renamed, not restyled.
export const FILTER_CTRL = 'filter-pill w-full h-9 rounded-full border px-3 text-xs font-medium bg-background focus:outline-none focus:ring-2 focus:ring-ring transition-colors appearance-none cursor-pointer'
export const FILTER_CTRL_DEFAULT = 'border-input text-muted-foreground'
export const FILTER_CTRL_ACTIVE  = 'border-primary text-primary bg-primary/5'


export function MultiSelect({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const hasSelection = selected.length > 0
  const displayLabel = hasSelection ? `${label} (${selected.length})` : label

  return (
    <div ref={ref} className="relative w-full">
      <button
        onClick={() => setOpen(o => !o)}
        className={`filter-pill ${FILTER_CTRL} flex items-center justify-between gap-1 ${hasSelection ? FILTER_CTRL_ACTIVE : FILTER_CTRL_DEFAULT}`}>
        <span className="truncate">{displayLabel}</span>
        <span className="shrink-0 text-xs">{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div ref={keepInView} className="absolute top-full mt-1 left-0 z-50 bg-card border border-border rounded-xl shadow-xl py-1 min-w-full min-w-[140px] max-h-64 overflow-y-auto">
          {options.map(opt => {
            const isSelected = selected.includes(opt)
            return (
              <button key={opt} onClick={() => onChange(isSelected ? selected.filter(s => s !== opt) : [...selected, opt])}
                className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors flex items-center gap-2">
                <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${isSelected ? 'bg-primary border-primary text-primary-foreground' : 'border-input'}`}>
                  {isSelected && <span className="text-xs font-bold"><Icon name="check" /></span>}
                </span>
                {opt}
              </button>
            )
          })}
          {hasSelection && (
            <>
              <div className="border-t border-border/50 my-1" />
              <button onClick={() => { onChange([]); setOpen(false) }}
                className="w-full text-left px-3 py-2 text-xs text-destructive hover:bg-accent transition-colors">
                {t('filter_clear')}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}




// Agent filter pill: same dropdown chrome as MultiSelect, but built to scale to a full
// roster — type-to-search, and active agents first with resigned agents below sorted by
// most-recently-left (mirrors the Agents tab's own "resigned" sort, agents_sort_resigned).
export function AgentMultiSelect({ agents, selected, onChange }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const ref = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  useEffect(() => { if (open) inputRef.current?.focus() }, [open])

  // Mobile → bottom sheet, not an absolute dropdown: this pill lives inside the Desk's
  // horizontally-scrollable pill slider (overflow-x-auto, which clips vertical overflow),
  // so an absolute dropdown was cut off (2026-07-18). Every other mobile pill (Sort/
  // Period/facets) already uses a bottom sheet; this brings the Agent pill in line.
  const isMobile = typeof window !== 'undefined' && !window.matchMedia('(min-width: 768px)').matches
  const hasSelection = selected.length > 0
  const displayLabel = hasSelection ? `Agent (${selected.length})` : 'Agent'

  const q = query.trim().toLowerCase()
  const filtered = q ? agents.filter(a => a.name.toLowerCase().includes(q)) : agents
  const active = filtered.filter(a => a.is_active !== false).sort((a, b) => a.name.localeCompare(b.name))
  const inactive = filtered.filter(a => a.is_active === false).sort((a, b) => (b.resign_date || '').localeCompare(a.resign_date || ''))

  // Selection is by agent ID (2026-07-28), not display name: names collide (18 live core
  // parties are called "irakli") and the Desk now sends the selection to the server as a
  // party-set lookup keyed on agents.id. The rows still SHOW names — only the value changed.
  const renderRow = a => {
    const isSelected = selected.includes(a.id)
    return (
      <button key={a.id} onClick={() => onChange(isSelected ? selected.filter(s => s !== a.id) : [...selected, a.id])}
        title={a.name}
        className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors flex items-center gap-2">
        <span className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${isSelected ? 'bg-primary border-primary text-primary-foreground' : 'border-input'}`}>
          {isSelected && <span className="text-xs font-bold"><Icon name="check" /></span>}
        </span>
        <span className="truncate">{a.name}</span>
      </button>
    )
  }

  const searchInput = (
    <input ref={inputRef} value={query} onChange={e => setQuery(e.target.value)} placeholder="Search agents…"
      className="w-full h-8 rounded-md border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring" />
  )
  const listBody = (<>
    {active.length > 0 && <div className="px-3 py-1 text-xs font-semibold uppercase text-muted-foreground">Active</div>}
    {active.map(renderRow)}
    {inactive.length > 0 && <div className="px-3 py-1 text-xs font-semibold uppercase text-muted-foreground border-t border-border/50 mt-1">Inactive</div>}
    {inactive.map(renderRow)}
    {active.length === 0 && inactive.length === 0 && (
      <div className="px-3 py-2 text-xs text-muted-foreground">No agents match</div>
    )}
  </>)
  const clearRow = hasSelection && (
    <button onClick={() => { onChange([]); setOpen(false) }}
      className="w-full text-left px-3 py-2 text-xs text-destructive hover:bg-accent transition-colors">
      {t('filter_clear')}
    </button>
  )

  const pill = (
    <button
      onClick={() => setOpen(o => !o)}
      className={`filter-pill ${FILTER_CTRL} flex items-center justify-between gap-1 ${hasSelection ? FILTER_CTRL_ACTIVE : FILTER_CTRL_DEFAULT}`}>
      <span className="truncate">{displayLabel}</span>
      <span className="shrink-0 text-xs">{open ? '▲' : '▼'}</span>
    </button>
  )

  // Mobile: bottom sheet (escapes the slider's overflow clip). Backdrop click closes;
  // the outside-click mousedown handler above stays harmless since the sheet is inside ref.
  if (isMobile) {
    return (
      <div ref={ref} className="relative w-full max-w-[140px]">
        {pill}
        {open && (
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)}>
            <div className="absolute inset-0 bg-black/50" />
            <div className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-card border-t border-border max-h-[70vh] flex flex-col"
              onClick={e => e.stopPropagation()}>
              <div className="flex items-center px-4 pt-3 pb-2 shrink-0">
                <span className="flex-1 text-base font-semibold">Agent</span>
                <button className="px-1 text-lg leading-none text-muted-foreground" aria-label={t('filter_clear')}
                  onClick={() => setOpen(false)}>✕</button>
              </div>
              <div className="px-3 pb-2 shrink-0">{searchInput}</div>
              <div className="flex-1 overflow-y-auto pb-2">{listBody}</div>
              {hasSelection && <div className="border-t border-border/50 shrink-0">{clearRow}</div>}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div ref={ref} className="relative w-full max-w-[140px]">
      {pill}
      {open && (
        <div ref={keepInView} className="absolute top-full mt-1 left-0 z-50 bg-card border border-border rounded-xl shadow-xl py-1 w-48 max-h-72 overflow-y-auto">
          <div className="px-2 pb-1 sticky top-0 bg-card">{searchInput}</div>
          {listBody}
          {hasSelection && (
            <>
              <div className="border-t border-border/50 my-1" />
              {clearRow}
            </>
          )}
        </div>
      )}
    </div>
  )
}


export function SingleSelect({ value, onChange, options, compact = false, onOptionHover = null }) {
  // `compact` (Desk mobile, 2026-07-18): the closed trigger shows the option's short
  // `compactLabel` (usually an emoji) instead of the full label, and the pill hugs its
  // content instead of filling the row — the open popup always shows full labels.
  //
  // OPTIONAL per-option fields, added 2026-07-31 for the Desk sort menu and inert for every
  // other caller (no field → same output as before):
  //   group / groupHint — start a labelled section here.
  //   hint              — a muted second line under one option.
  //   pillLabel         — what the CLOSED trigger shows, when the row's own label is too
  //                       short to stand alone out of the menu's context.
  //   altValue          — this row is REVERSIBLE: it owns two sort values. Selecting it
  //                       picks `value`; clicking it while already active switches to
  //                       `altValue` and back.
  //   dir / dirShort    — {desc, asc} direction words for a reversible row. The row's own
  //                       label NEVER changes with direction (a menu that rewrites the row
  //                       under the cursor reads as a different row); only this line does.
  //                       dirShort is the tail appended to the closed pill. No ▲▼ anywhere:
  //                       an arrow on a date never said which end it meant.
  //   advanced          — folded away behind a "more options" row until asked for (or
  //                       until it IS the current value — a hidden active sort would read
  //                       as the menu lying about what you are looking at).
  // ⚠️ A flip does NOT close the popup. In a dropdown (unlike a table header, where the
  // pattern comes from) the row you clicked is the only evidence the direction changed —
  // closing on flip makes a working control look like a no-op.
  const [open, setOpen] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)
  // onOptionHover (2026-07-31, Desk sort menu): fired after a 200ms dwell on an option —
  // the Desk uses it to PREFETCH the leads page for a sort before it is clicked, so the
  // click lands on a warm cache instead of a 3-5s corpus scan. Dwell-gated so sweeping the
  // cursor down the menu does not fire one heavy prefetch per row it passes over.
  const hoverTimer = useRef(null)
  const armHover = v => {
    if (!onOptionHover) return
    clearTimeout(hoverTimer.current)
    hoverTimer.current = setTimeout(() => onOptionHover(v), 200)
  }
  const disarmHover = () => clearTimeout(hoverTimer.current)
  const ref = useRef(null)

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // A reversible row is "current" for EITHER of its two values.
  const current = options.find(o => o.value === value || o.altValue === value)
  const isReversed = !!current?.altValue && current.altValue === value
  const isActive = value !== options[0]?.value
  const first = options[0]
  const triggerLabel = ((current?.pillLabel ?? current?.label) ?? (first?.pillLabel ?? first?.label))
    + (current?.dirShort ? ` · ${isReversed ? current.dirShort.asc : current.dirShort.desc}` : '')
  // Sections widen the popup: a hint line needs room to be read, and `min-w-full` alone
  // would size it to the trigger pill.
  const sectioned = options.some(o => o.group || o.hint)
  const hasAdvanced = options.some(o => o.advanced)
  // Never fold the option the user is actually on.
  const currentIsAdvanced = !!current?.advanced
  const visible = options.filter(o => !o.advanced || showAdvanced || currentIsAdvanced)

  return (
    <div ref={ref} className={`relative ${compact ? 'flex-1 min-w-0' : 'w-full'}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`filter-pill ${FILTER_CTRL} flex items-center justify-between gap-1 ${isActive ? FILTER_CTRL_ACTIVE : FILTER_CTRL_DEFAULT}`}>
        <span className="truncate">{(compact && (current?.compactLabel ?? first?.compactLabel)) || triggerLabel}</span>
        <span className="shrink-0 text-xs">{open ? '▲' : '▼'}</span>
      </button>
      {/* Compact pills sit at the line's right end and are narrower than the popup —
          left-anchoring pushed the popup past the viewport edge (horizontal page
          shift, user-reported 2026-07-18), so compact anchors RIGHT instead. */}
      {/* A SECTIONED popup is far wider than its trigger too, and this control sits near the
          right end of the Desk toolbar — left-anchoring pushed it off-screen (seen in the
          browser 2026-07-31, the same failure). It anchors right for the same reason. */}
      {open && (
        <div className={`absolute top-full mt-1 z-50 bg-card border border-border rounded-xl shadow-xl py-1 min-w-full max-h-[70vh] overflow-y-auto ${compact || sectioned ? 'right-0' : 'left-0'} ${compact ? 'min-w-[10rem] whitespace-nowrap' : ''} ${sectioned ? 'w-[20rem] max-w-[calc(100vw-2rem)]' : ''}`}>
          {visible.map((opt, i) => {
            const on = opt.value === value || opt.altValue === value
            const flips = on && !!opt.altValue
            return (
            <div key={opt.value}>
              {opt.group && (
                <div className={`px-3 pb-1 ${i === 0 ? 'pt-1.5' : 'pt-3 mt-1 border-t border-border/50'}`}>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{opt.group}</div>
                  {opt.groupHint && <div className="text-[11px] leading-snug text-muted-foreground/80 mt-0.5">{opt.groupHint}</div>}
                </div>
              )}
              <button title={flips ? t('desk_sort_flip_hint') : undefined}
                onMouseEnter={() => armHover(opt.value)} onMouseLeave={disarmHover}
                onClick={() => {
                  if (flips) { onChange(opt.altValue === value ? opt.value : opt.altValue); return }
                  onChange(opt.value); setOpen(false)
                }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors flex items-start gap-2">
                <span className={`w-3.5 h-3.5 mt-0.5 rounded-full border flex items-center justify-center shrink-0 ${on ? 'bg-primary border-primary' : 'border-input'}`}>
                  {on && <span className="w-1.5 h-1.5 rounded-full bg-primary-foreground inline-block" />}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block">{opt.label}</span>
                  {/* Direction shows only on the ACTIVE row: an inactive one is picked by
                      its default direction, so printing "Newest first" on all of them would
                      be noise claiming to be state. */}
                  {flips && opt.dir && (
                    <span className="block text-[11px] leading-snug text-primary mt-0.5">
                      {opt.altValue === value ? opt.dir.asc : opt.dir.desc} ⇄
                    </span>
                  )}
                  {opt.hint && <span className="block text-[11px] leading-snug text-muted-foreground whitespace-normal mt-0.5">{opt.hint}</span>}
                </span>
              </button>
            </div>
          )})}
          {hasAdvanced && !currentIsAdvanced && (
            <button onClick={() => setShowAdvanced(s => !s)}
              className="w-full text-left px-3 py-2 mt-1 border-t border-border/50 text-xs text-muted-foreground hover:bg-accent transition-colors">
              {showAdvanced ? t('desk_sort_less') : t('desk_sort_more')} {showAdvanced ? '▲' : '▾'}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// `third`: optional { id, icon, title } — an extra mode beyond card/list (e.g. the Desk's
// big-image gallery view). Omitted by every other caller, so this stays a no-op for them.
export function ViewToggle({ view, setView, third, hideList = false, hideCard = false, cardIcon = '⊞', cardTitle }) {
  // `third` accepts either one extra button ({id,icon,title}) or several (array) — IntakeTab
  // passes two (gallery + map) as of 2026-07-06, the other callers still pass a single object.
  // `hideList` drops the compact ☰ row view (Desk removed it 2026-07-16 — its single-column
  // card view IS the "list view" now). `hideCard` drops the first button too (Desk mobile,
  // 2026-07-18 — card and gallery render identically on a phone, so the pair was a fake
  // choice). `cardIcon`/`cardTitle` relabel the first (value 'card') button so Desk can
  // present it as "list view" without changing its internal value.
  const extras = third ? (Array.isArray(third) ? third : [third]) : []
  return (
    <div className="flex gap-0.5 bg-muted rounded-lg p-0.5">
      {!hideCard && (
      <button onClick={() => setView('card')} title={cardTitle || t('agents_view_card')}
        className={`px-2.5 py-1.5 rounded-md text-sm transition-all ${view === 'card' ? 'bg-card shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
        {cardIcon}
      </button>
      )}
      {!hideList && (
      <button onClick={() => setView('list')} {...{title: t('agents_view_list')}}
        className={`px-2.5 py-1.5 rounded-md text-sm transition-all ${view === 'list' ? 'bg-card shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
        ☰
      </button>
      )}
      {extras.map(x => (
        <button key={x.id} onClick={() => setView(x.id)} title={x.title}
          className={`px-2.5 py-1.5 rounded-md text-sm transition-all ${view === x.id ? 'bg-card shadow text-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
          {x.icon}
        </button>
      ))}
    </div>
  )
}

// ─── Weekly hours calc ────────────────────────────────────────────────────────
function weeklyHours(agent) {
  let officeMins = 0, homeMins = 0
  DAYS.forEach(d => {
    daySlots(agent, d).forEach(s => {
      const [sh, sm] = s.start.split(':').map(Number)
      const [eh, em] = s.end.split(':').map(Number)
      const m = (eh * 60 + em) - (sh * 60 + sm)
      if (m > 0) {
        if (s.location === 'office') officeMins += m
        else homeMins += m
      }
    })
  })
  return { officeMins, homeMins, totalMins: officeMins + homeMins }
}

// ─── 3-dot quick menu ─────────────────────────────────────────────────────────
function AgentQuickMenu({ agent, onEdit, onToggleActive, onRemoveNumber, onDelete, pstate, canManage = true, canDelete = false }) {
  // Only resigned agents whose number is still theirs get the standalone remove.
  // For active agents the number is reclaimed via the Mark-as-left dialog instead.
  const canRemove = canManage && agent.is_active === false && pstate?.state === 'held'
  const [open,        setOpen]        = useState(false)
  const [contactOpen, setContactOpen] = useState(false)
  const [copied,      setCopied]      = useState(false)
  const ref = useRef(null)
  // True once a concrete contact action (copy/call/…) happened in this menu session,
  // so closing doesn't also log a redundant "opened" entry.
  const interacted = useRef(false)

  const phone = agent.phone_work || agent.phone_personal
  const rawPhone = phone ? phone.replace(/[\s\-().]/g, '').replace(/^(00)?995/, '+995') : null

  // Audit who reached out to which agent and how (when captured by logEvent)
  function logContact(method) {
    logEvent(agent.id, agent.name, 'contact',
      t('log_contact').replace('{method}', method).replace('{phone}', rawPhone || '—').replace('{by}', getCurrentAdmin()))
  }

  // Close the submenu; if nothing was clicked, log that it was merely opened
  function closeContact() {
    setContactOpen(o => {
      if (o && !interacted.current) logContact(t('ct_opened'))
      return false
    })
  }
  function toggleContact() {
    if (contactOpen) closeContact()
    else { interacted.current = false; setContactOpen(true) }
  }
  function doContact(method) { interacted.current = true; logContact(method) }

  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); closeContact() } }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  async function copyNumber() {
    try { await navigator.clipboard.writeText(rawPhone) } catch {}
    setCopied(true)
    doContact(t('ct_copied'))
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <div ref={ref} className="relative" onClick={e => e.stopPropagation()}>
      <button onClick={() => { setOpen(o => !o); closeContact() }}
        className="w-7 h-7 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors text-lg leading-none">⋮</button>
      {open && (
        <div className="absolute left-0 top-8 z-40 bg-card border border-border rounded-xl shadow-xl py-1 min-w-[170px]">
          <button onClick={() => { onEdit(agent); setOpen(false) }}
            className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors">{t('qa_edit')}</button>

          {/* Contact submenu */}
          {phone && (
            <>
              <div className="border-t border-border/50 my-1" />
              <div className="relative">
                <button onClick={toggleContact}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors flex items-center justify-between">
                  <Icon name="phone" size={13} /> Contact <span className="text-muted-foreground text-xs">›</span>
                </button>
                {contactOpen && (
                  <div className="absolute left-full top-0 ml-1 z-50 bg-card border border-border rounded-xl shadow-xl py-1 min-w-[180px]">
                    <button onClick={copyNumber} title={t('ct_copied')}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors flex items-center justify-between gap-2">
                      <span className="tabular-nums font-medium">{rawPhone}</span>
                      <span className={`text-xs shrink-0 ${copied ? 'text-emerald-500' : 'text-muted-foreground'}`}><Icon name={copied ? 'check' : 'clipboard'} size={12} /></span>
                    </button>
                    <div className="border-t border-border/50 my-1" />
                    <a href={`tel:${rawPhone}`} onClick={() => doContact('Call')} className="block px-3 py-2 text-sm hover:bg-accent transition-colors"><Icon name="phone" size={13} /> Call</a>
                    <a href={`https://wa.me/${rawPhone.replace('+', '')}`} target="_blank" rel="noopener noreferrer"
                      onClick={() => doContact('WhatsApp')}
                      className="block px-3 py-2 text-sm hover:bg-accent transition-colors"><Icon name="message-circle" /> WhatsApp</a>
                    <a href={`sms:${rawPhone}`} onClick={() => doContact('SMS')} className="block px-3 py-2 text-sm hover:bg-accent transition-colors"><Icon name="mail" size={13} /> SMS</a>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Remove work number → returns it to the free pool (kept as history) */}
          {canRemove && (
            <>
              <div className="border-t border-border/50 my-1" />
              <button onClick={() => { onRemoveNumber?.(agent); setOpen(false) }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors text-destructive">
                <Icon name="trash" size={13} /> {t('qa_remove_number')}
              </button>
            </>
          )}

          {canManage && (
            <>
              <div className="border-t border-border/50 my-1" />
              <button onClick={() => { onToggleActive(agent); setOpen(false) }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors ${agent.is_active !== false ? 'text-destructive' : 'text-emerald-600 dark:text-emerald-400'}`}>
                {agent.is_active !== false ? t('qa_fire') : t('qa_activate')}
              </button>
            </>
          )}

          {/* Permanent delete — admin only (see canDelete in AgentsTab), separate from
              Mark-as-left/Reactivate above, which are reversible status changes. */}
          {canDelete && (
            <>
              <div className="border-t border-border/50 my-1" />
              <button onClick={() => {
                setOpen(false)
                if (window.confirm(t('agents_delete_confirm').replace('{name}', agent.name))) onDelete?.(agent.id)
              }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors text-destructive">
                <Icon name="trash" size={13} /> {t('qa_delete')}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Agent card ───────────────────────────────────────────────────────────────
function AgentCard({ agent, onClick, onEdit, onToggleActive, onRemoveNumber, onDelete, pstate, canManage = true, canDelete = false }) {
  const pmeta = phoneMeta(pstate)
  const counts = agentDaySummary(agent)
  const { officeMins, homeMins } = weeklyHours(agent)
  const isInactive = agent.is_active === false
  const age = agent.dob
    ? Math.floor((Date.now() - new Date(agent.dob).getTime()) / (365.25 * 24 * 3600 * 1000))
    : null
  const needsSetup = !agent.login_id || !agent.phone_work

  return (
    <div className={`bg-card border rounded-xl p-4 hover:border-primary/30 hover:shadow-sm transition-all group ${isInactive ? 'border-border/40' : needsSetup ? 'border-amber-400/60 dark:border-amber-600/40' : 'border-border'}`}>
      {/* Top row: 3-dot | name/role | contact right */}
      <div className="flex items-start gap-2 mb-3">
        <AgentQuickMenu agent={agent} onEdit={onEdit} onToggleActive={onToggleActive} onRemoveNumber={onRemoveNumber} onDelete={onDelete} pstate={pstate} canManage={canManage} canDelete={canDelete} />
        <button onClick={onClick} className={`flex-1 text-left min-w-0 flex items-start justify-between gap-2 ${isInactive ? 'opacity-70' : ''}`}>
          {/* Left: name + role + hire */}
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <div className="font-semibold text-sm group-hover:text-primary transition-colors">{agent.name}</div>
              {isInactive && <span className={`${COUNTER} font-semibold bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400`}>{t('agents_left_badge')}</span>}
              {!isInactive && needsSetup && <span className={`${COUNTER} font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400`}><Icon name="alert-triangle" /></span>}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {agent.role}{age && <span className="text-muted-foreground/60"> · {age}y</span>}
            </div>
            {agent.hire_date && (
              <div className="text-xs text-muted-foreground/50 mt-0.5">
                {t('agent_hired')} {new Date(agent.hire_date + 'T12:00:00').toLocaleDateString(dateLocale(), { day: 'numeric', month: 'short', year: 'numeric' })}
                {tenure(agent.hire_date) && <span className="ml-1">· {tenure(agent.hire_date)}</span>}
              </div>
            )}
          </div>
          {/* Right: contact */}
          <div className="text-xs text-muted-foreground text-right shrink-0 space-y-0.5">
            {isInactive
              ? pmeta && <div className={pmeta.cls} title={pmeta.title}><Icon name="phone" size={12} /> {fmtPhone(pstate.number)}</div>
              : agent.phone_work && <div><Icon name="phone" /> {fmtPhone(agent.phone_work)}</div>}
            {agent.phone_personal && <div><Icon name="smartphone" /> {fmtPhone(agent.phone_personal)}</div>}
            {agent.email          && <div className="truncate max-w-[120px]"><Icon name="mail" /> {agent.email}</div>}
            {!isInactive && !agent.phone_work && <div className="text-amber-600 dark:text-amber-400"><Icon name="alert-triangle" /> No phone</div>}
          </div>
        </button>
      </div>

      {/* Weekly dots */}
      <div className="flex gap-1.5 mb-2">
        {DAYS.map(d => {
          const slots     = daySlots(agent, d)
          const hasOffice = slots.some(s => s.location === 'office')
          const hasHome   = slots.some(s => s.location === 'home')
          const isSplit   = hasOffice && hasHome
          return (
            <div key={d} className="flex flex-col items-center gap-1">
              {isSplit ? (
                <div className="w-2.5 h-2.5 rounded-full overflow-hidden flex">
                  <div className="w-1/2 h-full bg-emerald-500" />
                  <div className="w-1/2 h-full bg-blue-400" />
                </div>
              ) : (
                <div className={`w-2.5 h-2.5 rounded-full ${hasOffice ? 'bg-emerald-500' : hasHome ? 'bg-blue-400' : 'bg-muted'}`} />
              )}
              <span className="text-xs text-muted-foreground">{(t('days')[DAYS.indexOf(d)] ?? d)[0]}</span>
            </div>
          )
        })}
      </div>

      {/* Badges + hours on one line */}
      <div className="flex items-center gap-1.5 flex-wrap text-xs">
        {counts.office > 0 && <span className={`${COUNTER} bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 font-medium`}>{counts.office}{t('badge_office')}</span>}
        {counts.home   > 0 && <span className={`${COUNTER} bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 font-medium`}>{counts.home}{t('badge_remote')}</span>}
        {counts.split  > 0 && <span className={`${COUNTER} bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 font-medium`}>{counts.split}{t('badge_split')}</span>}
        {counts.off    > 0 && <span className={`${COUNTER} bg-muted text-muted-foreground font-medium`}>{counts.off}{t('badge_off')}</span>}
        {officeMins > 0 && <span className="text-emerald-600 dark:text-emerald-400 font-medium ml-1">{fmtDuration(officeMins)} {t('agent_office_wk')}</span>}
        {homeMins   > 0 && <span className="text-blue-500 font-medium">{fmtDuration(homeMins)} {t('agent_remote_wk')}</span>}
      </div>

      {/* Resign info */}
      {agent.resign_date && (() => {
        const today = todayStr()
        const isFuture = agent.resign_date > today
        const displayDate = new Date(agent.resign_date + 'T12:00:00').toLocaleDateString(dateLocale(), { day: 'numeric', month: 'short', year: 'numeric' })
        return (
          <div className={`mt-2 text-xs rounded-lg px-2 py-1.5 ${
            isFuture
              ? 'text-amber-700 bg-amber-50 dark:bg-amber-900/10 dark:text-amber-400'
              : 'text-red-500 bg-red-50 dark:bg-red-900/10'
          }`}>
            {isFuture
              ? t('agents_leaving_badge').replace('{date}', displayDate)
              : `${t('agents_left_badge')} ${displayDate}`}
            {agent.resign_reason && <span className="text-muted-foreground ml-1">· {agent.resign_reason}</span>}
          </div>
        )
      })()}
    </div>
  )
}

// ─── Name helpers ─────────────────────────────────────────────────────────────
function tenure(hireDateStr) {
  if (!hireDateStr) return null
  const hire = new Date(hireDateStr + 'T12:00:00')
  const now  = new Date()
  const days = Math.floor((now - hire) / (1000 * 60 * 60 * 24))
  if (days < 30)  return `${days}d`
  const months = Math.floor(days / 30.4)
  if (months < 12) return `${months}mo`
  const years  = Math.floor(months / 12)
  const rem    = months % 12
  return rem > 0 ? `${years}y ${rem}mo` : `${years}y`
}

// ─── Agent list row ───────────────────────────────────────────────────────────
function AgentRow({ agent, onClick, onEdit, onToggleActive, onRemoveNumber, onDelete, pstate, canManage = true, canDelete = false }) {
  const pmeta = phoneMeta(pstate)
  const isInactive = agent.is_active === false
  const needsSetup = !agent.login_id || !agent.phone_work
  const { totalMins } = weeklyHours(agent)

  return (
    <div className={`bg-card border rounded-xl flex items-center gap-2 px-3 py-2.5 hover:border-primary/30 transition-all ${isInactive ? 'border-border/40' : needsSetup ? 'border-amber-400/60 dark:border-amber-600/40' : 'border-border'}`}>
      <AgentQuickMenu agent={agent} onEdit={onEdit} onToggleActive={onToggleActive} onRemoveNumber={onRemoveNumber} onDelete={onDelete} pstate={pstate} canManage={canManage} canDelete={canDelete} />
      <button onClick={onClick} className={`flex-1 text-left min-w-0 ${isInactive ? 'opacity-70' : ''}`}>
        {/* Mobile: everything on one line — firstName [badge] · role·tenure | phone */}
        <div className="flex items-center gap-1.5 sm:hidden overflow-hidden">
          {(() => {
            const parts = agent.name.trim().split(' ')
            const first = parts[0]
            const rest  = parts.slice(1).join(' ')
            return <>
              <span className="font-semibold text-sm whitespace-nowrap shrink-0">{first}</span>
              {rest && <span className="font-semibold text-sm text-muted-foreground truncate min-w-0">{rest}</span>}
            </>
          })()}
          {!isInactive && needsSetup && <span className={`${COUNTER} font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 shrink-0`}><Icon name="alert-triangle" /></span>}
          {isInactive && <span className={`${COUNTER} font-semibold bg-red-100 text-red-600 shrink-0`}>{t('agents_left_badge')}</span>}
          {!isInactive && agent.resign_date && agent.resign_date > todayStr() && (
            <span className={`${COUNTER} font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 shrink-0`}><Icon name="clock" /></span>
          )}
          <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0 ml-0.5">
            {agent.role}{agent.hire_date && tenure(agent.hire_date) && <span className="text-muted-foreground/40"> · {tenure(agent.hire_date)}</span>}
          </span>
          {isInactive
            ? pmeta && <div className={`text-xs whitespace-nowrap shrink-0 ml-auto ${pmeta.cls}`} title={pmeta.title}>{fmtPhone(pstate.number)}</div>
            : agent.phone_work && <div className="text-xs text-muted-foreground/60 whitespace-nowrap shrink-0 ml-auto">{fmtPhone(agent.phone_work)}</div>}
        </div>

        {/* Desktop: full info on one line */}
        <div className="hidden sm:grid grid-cols-[160px_1fr_auto] items-center gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-sm truncate">{agent.name}</span>
              {!isInactive && needsSetup && <span className={`${COUNTER} font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 shrink-0`}><Icon name="alert-triangle" /></span>}
              {isInactive && <span className={`${COUNTER} font-semibold bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400 shrink-0`}>{t('agents_left_badge')}</span>}
              {!isInactive && agent.resign_date && agent.resign_date > todayStr() && (
                <span className={`${COUNTER} font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 shrink-0`}><Icon name="clock" /> {new Date(agent.resign_date + 'T12:00:00').toLocaleDateString(dateLocale(), { day: 'numeric', month: 'short' })}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 min-w-0">
            {DAYS.map(d => {
              const slots     = daySlots(agent, d)
              const hasOffice = slots.some(s => s.location === 'office')
              const hasHome   = slots.some(s => s.location === 'home')
              const isSplit   = hasOffice && hasHome
              return (
                <div key={d} className="flex flex-col items-center gap-0.5 shrink-0">
                  {isSplit ? (
                    <div className="w-2.5 h-2.5 rounded-full overflow-hidden flex">
                      <div className="w-1/2 h-full bg-emerald-500" />
                      <div className="w-1/2 h-full bg-blue-400" />
                    </div>
                  ) : (
                    <div className={`w-2.5 h-2.5 rounded-full ${hasOffice ? 'bg-emerald-500' : hasHome ? 'bg-blue-400' : 'bg-muted'}`} />
                  )}
                  <span className="text-xs text-muted-foreground">{(t('days')[DAYS.indexOf(d)] ?? d)[0]}</span>
                </div>
              )
            })}
            <span className="text-xs text-muted-foreground shrink-0">{agent.role}{agent.hire_date && tenure(agent.hire_date) && <span className="text-muted-foreground/40 ml-1">· {tenure(agent.hire_date)}</span>}</span>
            {(() => {
              const counts = agentDaySummary(agent)
              const { officeMins, homeMins } = weeklyHours(agent)
              return <>
                {counts.office > 0 && <span className={`${COUNTER} bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 font-medium shrink-0`}>{counts.office}{t('badge_office')}</span>}
                {counts.home   > 0 && <span className={`${COUNTER} bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 font-medium shrink-0`}>{counts.home}{t('badge_remote')}</span>}
                {counts.split  > 0 && <span className={`${COUNTER} bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 font-medium shrink-0`}>{counts.split}{t('badge_split')}</span>}
                {counts.off    > 0 && <span className={`${COUNTER} bg-muted text-muted-foreground font-medium shrink-0`}>{counts.off}{t('badge_off')}</span>}
                {officeMins > 0 && <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium shrink-0">{fmtDuration(officeMins)} {t('agent_office_wk')}</span>}
                {homeMins   > 0 && <span className="text-xs text-blue-500 font-medium shrink-0">{fmtDuration(homeMins)} {t('agent_remote_wk')}</span>}
              </>
            })()}
          </div>
          <div className="text-right">
            {isInactive
              ? pmeta && <div className={`text-xs whitespace-nowrap ${pmeta.cls}`} title={pmeta.title}>{fmtPhone(pstate.number)}</div>
              : agent.phone_work && <div className="text-xs text-muted-foreground/60 whitespace-nowrap">{fmtPhone(agent.phone_work)}</div>}
          </div>
        </div>
      </button>
    </div>
  )
}

// ─── Sort options ─────────────────────────────────────────────────────────────
const SORT_OPTIONS = [
  { id: 'name',     label: () => t('agents_sort_name')     },
  { id: 'newest',   label: () => t('agents_sort_newest')   },
  { id: 'oldest',   label: () => t('agents_sort_oldest')   },
  { id: 'resigned', label: () => t('agents_sort_resigned') },
  { id: 'hours',    label: () => t('agents_sort_hours')    },
  { id: 'role',     label: () => t('agents_sort_role')     },
]

function sortAgents(agents, sort) {
  const list = [...agents]
  if (sort === 'name')     return list.sort((a, b) => a.name.localeCompare(b.name))
  if (sort === 'newest')   return list.sort((a, b) => (b.hire_date || '').localeCompare(a.hire_date || ''))
  if (sort === 'oldest')   return list.sort((a, b) => (a.hire_date || '9999').localeCompare(b.hire_date || '9999'))
  if (sort === 'resigned') return list.sort((a, b) => (b.resign_date || '').localeCompare(a.resign_date || ''))
  if (sort === 'hours')    return list.sort((a, b) => weeklyHours(b).totalMins - weeklyHours(a).totalMins)
  if (sort === 'role')     return list.sort((a, b) => a.role.localeCompare(b.role) || a.name.localeCompare(b.name))
  return list
}

// ─── Main component ───────────────────────────────────────────────────────────
export function AgentsTab({ role, roleAssignedAt, roleHistory = [], myOfficeId = null, agents, allAgents = [], offices, officeFilterActive = false, onClearOfficeFilter = null, config = {}, phones = [], phoneHistory = {}, createAgent, editAgent, removeAgent, newlyHiredAgent, onNewlyHiredClear }) {
  const [modal,      setModal]      = useState(null)

  function openAgent(agent) {
    if (!agent || agent === 'new') { setModal(agent); return }
    try {
      const entry = localStorage.getItem(`hire_ts_agent_${agent.id}`)
      if (entry) {
        const { ts, candidateId, preHireStatus } = JSON.parse(entry)
        const remaining = 300 - Math.floor((Date.now() - Number(ts)) / 1000)
        if (remaining > 0) {
          setModal({ ...agent, _revertHire: true, _candidateId: candidateId, _preHireStatus: preHireStatus })
          return
        }
      }
    } catch {}
    setModal(agent)
  }

  // Auto-open newly hired agent
  useEffect(() => {
    if (newlyHiredAgent) {
      const agent = agents.find(a => a.id === newlyHiredAgent.id) || newlyHiredAgent
      setModal({ ...agent, _revertHire: true, _candidateId: newlyHiredAgent._candidateId, _preHireStatus: newlyHiredAgent._preHireStatus })
    }
  }, [newlyHiredAgent])
  const [sort,       setSort]       = useState('newest')
  const [view,       setView]       = useViewPreference('agents')
  const [filterDist, setFilterDist] = useState([])
  const [filterLang, setFilterLang] = useState([])

  const [agentFilter, setAgentFilter] = useState('active') // 'active' | 'left' | 'all'

  // When switching to the Left/Resigned view, default the sort to most-recently
  // resigned; restore newest-hired when leaving it. Skips the first render so an
  // explicit sort choice isn't clobbered on mount.
  const didMountFilter = useRef(false)
  useEffect(() => {
    if (!didMountFilter.current) { didMountFilter.current = true; return }
    setSort(agentFilter === 'left' ? 'resigned' : 'newest')
  }, [agentFilter])

  const [filterSetup, setFilterSetup] = useState(false)
  const [search,      setSearch]      = useState('')
  const [toast,       setToast]       = useState(null) // { msg, type: 'info'|'warn' }
  const [markLeft,    setMarkLeft]    = useState(null) // agent pending mark-as-left
  const toastTimerRef = useRef(null)

  function showToast(msg, type = 'info') {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    setToast({ msg, type })
    toastTimerRef.current = setTimeout(() => setToast(null), 7000)
  }

  // Review 2026-09-23: login lock/create/update failures (from this tab, AgentModal or
  // useSchedule) arrive here as an event and show as the warn toast, not console-only.
  useEffect(() => {
    const onFail = e => showToast(e.detail?.msg || t('common_action_failed'), 'warn')
    window.addEventListener(AGENT_SYNC_FAILED_EVENT, onFail)
    return () => window.removeEventListener(AGENT_SYNC_FAILED_EVENT, onFail)
  }, [])

  // general_manager/admin see the company's full resignation history. manager/agent_hr are
  // scoped to resignations that happened during THEIR OWN past stints in that role — not
  // hidden outright, just no history from before/outside those stints. Uses the append-only
  // role_history log (every period they've held `role`, current period has ended_at=null) so
  // a demote-then-repromote restores what they'd already seen, rather than resetting to zero
  // like a single overwritten timestamp would. Falls back to the simpler roleAssignedAt
  // cutoff (single latest promotion) only if the history table hasn't been migrated live yet
  // — see getMyRoleHistory in db/index.js. Dates compared as bare dates (resign_date has no
  // time component).
  const canSeeLeft = role === 'admin' || role === 'general_manager'
  const roleSinceDate = roleAssignedAt ? roleAssignedAt.slice(0, 10) : null
  const rolePeriods = useMemo(
    () => roleHistory.filter(h => h.role === role).map(h => ({
      start: h.started_at.slice(0, 10),
      end: h.ended_at ? h.ended_at.slice(0, 10) : null,
    })),
    [roleHistory, role]
  )

  const visibleResigned = useMemo(() => {
    if (canSeeLeft) return agents.filter(a => a.is_active === false)
    if (rolePeriods.length) {
      return agents.filter(a => a.is_active === false && a.resign_date &&
        rolePeriods.some(p => a.resign_date >= p.start && (!p.end || a.resign_date <= p.end)))
    }
    if (!roleSinceDate) return []
    return agents.filter(a => a.is_active === false && a.resign_date && a.resign_date >= roleSinceDate)
  }, [agents, canSeeLeft, rolePeriods, roleSinceDate])

  const pool = useMemo(() => {
    const activeAgents = agents.filter(a => a.is_active !== false)
    if (agentFilter === 'left') return visibleResigned
    if (agentFilter === 'all')  return [...activeAgents, ...visibleResigned]
    return activeAgents
  }, [agents, agentFilter, visibleResigned])

  const filtered = useMemo(() => {
    let list = pool
    if (filterDist.length)  list = list.filter(a => filterDist.includes(a.district))
    if (filterLang.length)  list = list.filter(a => Array.isArray(a.languages) && a.languages.some(l => filterLang.includes(l)))
    if (filterSetup) list = list.filter(a => !a.login_id || !a.phone_work)
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(a =>
        a.name.toLowerCase().includes(q) ||
        (a.role || '').toLowerCase().includes(q) ||
        (a.phone_work || '').includes(q) ||
        (a.email || '').toLowerCase().includes(q)
      )
    }
    return sortAgents(list, sort)
  }, [pool, sort, filterDist, filterLang, filterSetup, search])

  // Listen for global add-agent event from the + button in App
  useEffect(() => {
    const handler = () => setModal('new')
    window.addEventListener('sentavus:add-agent', handler)
    return () => window.removeEventListener('sentavus:add-agent', handler)
  }, [])

  async function handleSave(data) {
    const today = todayStr()
    const resignDate = data.resign_date || null

    if (modal === 'new') {
      const agent = await createAgent(data)
      return agent
    }

    // Detect resign date changes
    const prevAgent = agents.find(a => a.id === modal.id)
    const prevResignDate = prevAgent?.resign_date || null
    const resignDateChanged = resignDate !== prevResignDate

    let extraUpdates = {}
    let toastMsg = null
    let toastType = 'info'

    if (resignDateChanged && resignDate) {
      if (resignDate > today) {
        // Future date — keep active, schedule for auto-resign
        extraUpdates = { is_active: true }
        const displayDate = new Date(resignDate + 'T12:00:00').toLocaleDateString(dateLocale(), { day: 'numeric', month: 'long', year: 'numeric' })
        toastMsg = t('agents_resign_future_toast').replace('{name}', data.name).replace('{date}', displayDate)
        toastType = 'warn'
      } else {
        // Past or today — mark as resigned immediately
        extraUpdates = { is_active: false }
        const displayDate = new Date(resignDate + 'T12:00:00').toLocaleDateString(dateLocale(), { day: 'numeric', month: 'long', year: 'numeric' })
        if (resignDate === today) {
          toastMsg = t('agents_resign_today_toast').replace('{name}', data.name)
        } else {
          toastMsg = t('agents_resign_past_toast').replace('{name}', data.name).replace('{date}', displayDate)
        }
        toastType = 'info'
      }
    } else if (!resignDate && prevResignDate && prevAgent?.is_active === false) {
      // Resign date cleared — reactivate. THE START DATE MOVES TO TODAY (owner, 2026-09-29: "make sure
      // next time anyone gets reinstated its date also changes"): statistics, the ranking, the floor
      // and the silent-day check all start from hire_date, so without this a returning agent's time
      // away counts against them. The previous date survives in the agent log (editAgent diffs it).
      extraUpdates = { is_active: true, hire_date: today }
    }

    const result = await editAgent(modal.id, { ...data, ...extraUpdates })
    if (toastMsg) showToast(toastMsg, toastType)
    // Locking on a fresh resignation is now handled by AgentModal's own confirmMarkLeft
    // (it intercepts plain Save too, per the dialog it already shows) — respects the
    // dialog's "lock login" checkbox, so it must NOT also be auto-applied here.
    // Reactivating still has no dialog (nothing risky to confirm) — unlock stays automatic.
    const loginId = data.login_id ?? prevAgent?.login_id
    if (loginId && extraUpdates.is_active === true && prevAgent?.is_active === false) {
      lockLoginReported(loginId, false, data.name)  // review 2026-09-23: failure reported, not console-only
    }
    return result
  }

  function handleToggleActive(agent) {
    const by = getCurrentAdmin()
    // Reactivating is immediate; marking-as-left opens the dialog (so the admin
    // can also choose to return the work number to the pool).
    if (agent.is_active === false) {
      // 🔴 THE RESIGN DATE MUST GO WITH IT (owner-reported 2026-09-10: reinstated an agent, then
      // "when I want to save its info update it, it asks this" — the mark-as-left dialog).
      // This wrote `is_active: true` alone, leaving a PAST `resign_date` on an active agent. The
      // modal's Save then reads exactly that shape — not resigned, but carrying a resign date in
      // the past — as "you are firing this person", and re-opens the dialog. The admin was
      // trapped: confirming re-archived the agent they had just reinstated, cancelling blocked
      // the edit they came to make.
      // ⚠️ `resign_reason` goes too. A reason without a date is a sentence about something that
      // did not happen, and it would surface on the next archive as if it were this one's.
      // 📌 The OTHER reactivate path (the modal, "resign date cleared -> reactivate") was always
      // correct — it reactivates BECAUSE the date was cleared. Only this quick action set the
      // flag without the fact behind it.
      // Reinstatement restarts the record from today — see the modal path above for why.
      editAgent(agent.id, { is_active: true, resign_date: null, resign_reason: null, hire_date: todayStr() },
        t('agents_reactivated_log').replace('{by}', by))
      // Undo of Mark-as-left's lock (if it was locked) — rehiring must restore access
      // without needing a new password. Non-blocking; a rehired agent still gets a
      // working account even if this call fails, they'd just need a manual unlock.
      if (agent.login_id) {
        lockLoginReported(agent.login_id, false, agent.name)  // review 2026-09-23: failure reported
      }
      return
    }
    setMarkLeft(agent)
  }

  // Confirmed from the Mark-as-left dialog. Marks resigned and, when chosen,
  // clears phone_work (frees the inventory number), returns the agent's property
  // cards to the pool, and/or locks their login on both auth systems.
  // Review 2026-09-23: the roster write is AWAITED before the lock — they used to run in
  // parallel, so a failed write left a locked login on an agent the roster calls active
  // (plus an unhandled rejection). And firing NOW records today: a stored FUTURE resign
  // date is a schedule, not the day they left.
  async function confirmMarkLeft({ removeNumber, moveCards, lockLogin }) {
    const agent = markLeft
    if (!agent) return
    setMarkLeft(null)
    const today = todayStr()
    try {
      await editAgent(agent.id, {
        is_active: false,
        resign_date: (agent.resign_date && agent.resign_date <= today) ? agent.resign_date : today,
        ...(removeNumber ? { phone_work: '' } : {}),
      })
    } catch (e) { reportAgentSyncFailure(agent.name, e); return }
    if (moveCards && agent.login_id) {
      releaseAgentDrafts(agent.login_id).catch(e => reportAgentSyncFailure(agent.name, e))
    }
    if (lockLogin && agent.login_id) await lockLoginReported(agent.login_id, true, agent.name)
  }

  // Quick "remove number": return the work number to the free pool. editAgent
  // clears phone_work (so it won't auto-reattach), frees the inventory row, and
  // records the change to the agent's Timeline log. The number then drops off
  // the card — its history stays in the Timeline.
  function handleRemoveNumber(agent) {
    if (!agent.phone_work) return
    const num = fmtPhone(agent.phone_work)
    if (!confirm(t('agents_remove_number_confirm').replace('{number}', num).replace('{name}', agent.name))) return
    editAgent(agent.id, { phone_work: '' })
  }

  const districts = config.districts || []
  const languages = config.languages || []
  const activeFilters = filterDist.length + filterLang.length + (filterSetup ? 1 : 0)
  const setupCount = agents.filter(a => a.is_active !== false && (!a.login_id || !a.phone_work)).length

  return (
    <div>
      {/* Toast notification */}
      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] flex items-start gap-3 px-4 py-3 rounded-xl shadow-xl border max-w-sm w-full text-sm font-medium transition-all
          ${toast.type === 'warn'
            ? 'bg-amber-50 border-amber-300 text-amber-900 dark:bg-amber-900/30 dark:border-amber-600 dark:text-amber-200'
            : 'bg-emerald-50 border-emerald-300 text-emerald-900 dark:bg-emerald-900/30 dark:border-emerald-600 dark:text-emerald-200'
          }`}>
          <span className="text-base shrink-0"><Icon name={toast.type === 'warn' ? 'clock' : 'check-circle'} size={16} /></span>
          <span className="flex-1">{toast.msg}</span>
          <button onClick={() => setToast(null)} className="shrink-0 text-lg leading-none opacity-50 hover:opacity-100">×</button>
        </div>
      )}
      {/* Filter bar — row 1: view toggle + search; row 2: filter pills */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2">
          <ViewToggle view={view} setView={setView} />
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-xs"><Icon name="search" /></span>
            <input value={search} onChange={e => setSearch(e.target.value)}
              {...{placeholder: t('agents_search_placeholder')}}
              className="w-full rounded-full border border-input pl-8 pr-3 py-1.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <button onClick={() => setModal('new')}
            className="flex items-center gap-1 shrink-0 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity whitespace-nowrap">
            {/* mobile: icon-only "+" to leave room for the search bar */}
            <span className="sm:hidden text-base leading-none">+</span>
            <span className="hidden sm:inline">+ {t('agents_add')}</span>
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <SingleSelect
            value={agentFilter}
            onChange={setAgentFilter}
            options={[
              { value: 'active', label: `${t('agents_filter_active')} (${agents.filter(a => a.is_active !== false).length})` },
              { value: 'left', label: `${t('agents_filter_left')} (${visibleResigned.length})` },
              { value: 'all',  label: `${t('agents_filter_all')} (${agents.filter(a => a.is_active !== false).length + visibleResigned.length})` },
            ]}
          />
          <MultiSelect label={t('agents_filter_district')} options={districts} selected={filterDist} onChange={setFilterDist} />
          <MultiSelect label={t('agents_filter_language')} options={languages} selected={filterLang} onChange={setFilterLang} />
          <SingleSelect
            value={sort}
            onChange={setSort}
            options={SORT_OPTIONS.map(o => ({ value: o.id, label: o.label() }))}
          />
        </div>
      </div>

      {/* Results count when filtered */}
      {activeFilters > 0 && (
        <div className="text-xs text-muted-foreground mb-3">
          Showing {filtered.length} of {pool.length} agents
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-muted-foreground text-sm gap-3">
          <Icon size={40} className="text-muted-foreground"
            name={activeFilters > 0 || officeFilterActive ? 'search' : agentFilter === 'left' ? 'folder' : 'user'} />
          {/* "Filtered to zero" must never masquerade as "genuinely zero agents" — the top-bar
              office filter empties this tab silently otherwise (2026-07-13 UI audit). */}
          {officeFilterActive && activeFilters === 0
            ? <p>{t('agents_empty_office_filtered')}</p>
            : <p>{activeFilters > 0 ? t('agents_no_match') : agentFilter === 'left' ? t('agents_none_left') : t('agents_none_yet')}</p>}
          {activeFilters > 0 && (
            <button onClick={() => { setFilterDist([]); setFilterLang([]) }}
              className={pill('none', { door: true, extra: 'border border-border font-medium hover:bg-accent transition-colors' })}>
              {t('filter_clear_all')}
            </button>
          )}
          {officeFilterActive && onClearOfficeFilter && (
            <button onClick={onClearOfficeFilter}
              className={pill('none', { door: true, extra: 'border border-primary/50 text-primary font-medium hover:bg-primary/10 transition-colors' })}>
              <Icon name="building" size={13} /> {t('office_filter_show_all')}
            </button>
          )}
        </div>
      ) : view === 'card' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(agent => (
            <AgentCard
              key={agent.id}
              agent={agent}
              onClick={() => openAgent(agent)}
              onEdit={() => openAgent(agent)}
              onToggleActive={handleToggleActive}
              onRemoveNumber={handleRemoveNumber}
              onDelete={removeAgent}
              pstate={phoneState(agent, phones, agents, phoneHistory)}
              canManage={canSeeLeft || agent.is_active !== false}
              canDelete={role === 'admin'}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {filtered.map(agent => (
            <AgentRow
              key={agent.id}
              agent={agent}
              onClick={() => openAgent(agent)}
              onEdit={() => openAgent(agent)}
              onToggleActive={handleToggleActive}
              onRemoveNumber={handleRemoveNumber}
              onDelete={removeAgent}
              pstate={phoneState(agent, phones, agents, phoneHistory)}
              canManage={canSeeLeft || agent.is_active !== false}
              canDelete={role === 'admin'}
            />
          ))}
        </div>
      )}

      {modal && (
        <AgentModal
          viewerRole={role}
          myOfficeId={myOfficeId}
          allAgents={allAgents.length ? allAgents : agents}
          agent={modal === 'new' ? null : modal}
          offices={offices}
          config={config}
          phones={phones.map(p => { const o = (allAgents.length ? allAgents : agents).find(a => a.id === p.agent_id); return { ...p, agentName: o?.name, agentOfficeId: o?.office_id ?? null } })}
          onSave={handleSave}
          onDelete={removeAgent}
          showRevertHire={modal?._revertHire}
          candidateId={modal?._candidateId}
          preHireStatus={modal?._preHireStatus}
          onClose={(signal) => {
            const candId = modal?._candidateId
            setModal(null)
            if (signal === 'revert' && candId) {
              onNewlyHiredClear?.({ revertCandidateId: candId })
            } else {
              onNewlyHiredClear?.()
            }
          }}
        />
      )}

      {markLeft && (
        <MarkLeftDialog
          name={markLeft.name}
          numberLabel={markLeft.phone_work ? fmtPhone(markLeft.phone_work) : null}
          loginId={markLeft.login_id || null}
          onConfirm={confirmMarkLeft}
          onCancel={() => setMarkLeft(null)}
        />
      )}
    </div>
  )
}
